import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';
import chatWebsocket from '../services/chatWebsocket';
import useAuthStore from '../store/useAuthStore';
import { showBrowserNotification, getPermission, setupPushSubscription } from '../services/notificationService';

export default function ChatNotificationPopup() {
  const [popup, setPopup] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const timerRef = useRef(null);
  const locationRef = useRef(location.pathname);
  const userRef = useRef(user);

  useEffect(() => { locationRef.current = location.pathname; }, [location.pathname]);
  useEffect(() => { userRef.current = user; }, [user]);

  // Setup Web Push subscription as soon as notification permission is granted
  useEffect(() => {
    if (getPermission() === 'granted') {
      setupPushSubscription();
    }
  }, []);

  useEffect(() => {
    const handleNewMessage = (data) => {
      if (!data.message) return;
      const msg = data.message;
      const currentUser = userRef.current;

      // Ignore own messages
      const myId = currentUser?.id || currentUser?.userId;
      if (msg.senderId === myId) return;

      const senderName =
        msg.sender?.displayName || msg.sender?.username || msg.sender?.fullName || 'Новое сообщение';

      let msgPreview;
      if (msg.messageType === 'image') msgPreview = '📷 Фото';
      else if (msg.messageType === 'audio') msgPreview = '🎵 Голосовое сообщение';
      else msgPreview = (msg.content || '').slice(0, 80) || '—';

      // Native browser notification when tab is hidden
      if (document.visibilityState === 'hidden' && getPermission() === 'granted') {
        showBrowserNotification(senderName, {
          body: msgPreview,
          tag: `chat-${msg.senderId || msg.groupId || msg.channelId}`,
        });
      }

      // In-app popup when not on chat page (even if tab is visible)
      if (!locationRef.current.startsWith('/chat')) {
        setPopup({
          senderName,
          message: msgPreview,
          avatar: msg.sender?.avatar,
          initial: (senderName[0] || '?').toUpperCase(),
        });
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setPopup(null), 4500);
      }
    };

    chatWebsocket.on('new_message', handleNewMessage);
    return () => {
      chatWebsocket.off('new_message', handleNewMessage);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!popup) return null;

  const handleClick = () => {
    setPopup(null);
    navigate('/chat');
  };

  const handleClose = (e) => {
    e.stopPropagation();
    if (timerRef.current) clearTimeout(timerRef.current);
    setPopup(null);
  };

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-24px)] max-w-sm cursor-pointer select-none"
      style={{ animation: 'slideDownFadeIn 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}
      onClick={handleClick}
    >
      <div className="bg-white/95 backdrop-blur-xl rounded-[20px] px-4 py-3 shadow-2xl border border-slate-100 flex items-center gap-3">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-[16px] shrink-0 overflow-hidden">
          {popup.avatar
            ? <img src={popup.avatar} alt="" className="w-full h-full object-cover" />
            : popup.initial}
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-bold text-slate-900 truncate leading-tight">{popup.senderName}</p>
          <p className="text-[12px] text-slate-500 truncate leading-snug mt-0.5">{popup.message}</p>
        </div>

        {/* Icons */}
        <div className="flex items-center gap-1 shrink-0">
          <MessageSquare className="w-3.5 h-3.5 text-blue-400" />
          <button
            type="button"
            onClick={handleClose}
            className="p-1 rounded-full hover:bg-slate-100 transition-colors"
          >
            <X className="w-3 h-3 text-slate-400" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideDownFadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-16px) scale(0.94); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0)    scale(1); }
        }
      `}</style>
    </div>
  );
}
